import Hls from 'hls.js';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Comment, commentsApi } from 'src/api/comments';
import useAppSelector from 'src/hooks/Redux/useAppSelector';
import { selectCurrentUserId } from 'src/redux/slices/loggedUserSlice';
import { DateUtils } from 'src/utils/date.utils';
import styles from './CommentItem.module.scss';
import { CommentReplies } from './CommentReplies/CommentReplies';
import { CommentActions } from './CommentsActions/CommentsActions';
import { CommentContent } from './CommentsContent/CommentsContent';
import { CommentHeader } from './CommentsHeader/CommentsHeader';

interface CommentItemProps {
  comment: Comment;
  replies: Comment[];
  postId: string;
  onDelete: (commentId: string) => void;
  onEdit: (commentId: string, newText: string) => void;
  setCommentsCount?: React.Dispatch<React.SetStateAction<number>>;
}

export const CommentItem: FC<CommentItemProps> = ({
  comment,
  replies,
  postId,
  onDelete,
  onEdit,
  setCommentsCount,
}) => {
  const [showReplies, setShowReplies] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(comment.like_count);
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(comment.text);
  const [localReplies, setLocalReplies] = useState(replies);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const isLiking = useRef(false);
  const userId = useAppSelector(selectCurrentUserId);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setLiked(userId != null && comment.liked_by.includes(userId));
  }, [comment.liked_by, userId]);

  useEffect(() => {
    let hls: Hls | undefined;

    if (comment.media_type === 'video' && Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(comment.media_url ?? '');
      hls.attachMedia(videoRef.current!);
    } else if (
      comment.media_type === 'video' &&
      videoRef.current?.canPlayType('application/vnd.apple.mpegurl')
    ) {
      videoRef.current.src = comment.media_url ?? '';
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, [comment.media_url, comment.media_type]);

  const handleLike = async () => {
    if (isLiking.current) {
      return;
    }
    isLiking.current = true;

    const wasLiked = liked;
    setLiked(!wasLiked);
    setLikeCount((prev) => prev + (wasLiked ? -1 : 1));

    try {
      await commentsApi.toggleLikeComment(postId, comment._id);
    } catch (error) {
      setLiked(wasLiked);
      setLikeCount((prev) => prev + (wasLiked ? 1 : -1));
      console.error('Error liking the comment:', error);
    } finally {
      isLiking.current = false;
    }
  };

  const handleVideoClick = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  };

  const toggleReplies = () => setShowReplies((prev) => !prev);

  const handleNewReply = (newReply: Comment) => {
    setLocalReplies((prevReplies) => [...prevReplies, newReply]);
    if (setCommentsCount) {
      setCommentsCount((prev) => prev + 1);
    }
  };

  const handleEditClick = () => setIsEditing(true);

  const handleDeleteClick = async () => {
    try {
      await commentsApi.deleteComment(postId, comment._id);
      onDelete(comment._id);
    } catch (error) {
      console.error('Error deleting the comment:', error);
    }
  };

  const handleDeleteReply = (replyId: string) => {
    setLocalReplies((prev) =>
      prev.map((r) => (r._id === replyId ? { ...r, is_deleted: true } : r))
    );
    onDelete(replyId);
  };

  const handleSaveEdit = async () => {
    try {
      await commentsApi.updateCommentText(postId, comment._id, {
        text: editedText,
      });
      onEdit(comment._id, editedText);
      setIsEditing(false);
    } catch (error) {
      console.error('Error editing the comment:', error);
    }
  };

  return (
    <div className={styles.commentItem}>
      <CommentHeader
        userId={userId}
        comment={comment}
        liked={liked}
        likeCount={likeCount}
        timestamp={DateUtils.timeAgo(comment.createdAt)}
        anchorEl={anchorEl}
        onLike={handleLike}
        onClickMore={(e: React.MouseEvent<HTMLElement>) =>
          setAnchorEl(e.currentTarget)
        }
        onClosePopover={() => setAnchorEl(null)}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
      />
      <CommentContent
        comment={comment}
        isEditing={isEditing}
        editedText={editedText}
        setEditedText={setEditedText}
        videoRef={videoRef}
        handleVideoClick={handleVideoClick}
        handleSaveEdit={handleSaveEdit}
      />
      <CommentActions
        postId={postId}
        commentId={comment._id}
        isDeleted={comment.is_deleted}
        isHasParent={!!comment.parent_id}
        onCommentPosted={handleNewReply}
      />
      <CommentReplies
        showReplies={showReplies}
        localReplies={localReplies}
        toggleReplies={toggleReplies}
        postId={postId}
        onDelete={handleDeleteReply}
        onEdit={onEdit}
      />
    </div>
  );
};
